import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Mail, 
  Send,
  AlertCircle,
  CheckCircle
} from "lucide-react";
import { agentAdvancerSenzaSfondo } from '../images';
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setError("Email richiesta");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Email non valida");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/api/v1/auth/forgot-password`, null, {
        params: { email }
      });
      
      setSubmitted(true);
      toast.success("Email inviata! Controlla la tua casella di posta");
    } catch (err) {
      console.error("Errore forgot password:", err);
      // Non mostrare errori specifici per sicurezza
      setSubmitted(true);
      toast.success("Email inviata! Controlla la tua casella di posta");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-28 md:pb-8">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="rounded-xl"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna al Login
            </Button>
            
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center p-1">
                <img 
                  src={agentAdvancerSenzaSfondo} 
                  alt="InLeads Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="font-semibold text-gray-900">InLeads</span>
            </div>
          </div>

          {/* Success Message */}
          <div className="max-w-md mx-auto">
            <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-green-500 to-blue-600 text-white p-8 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold">
                  Email Inviata!
                </CardTitle>
              </CardHeader>

              <CardContent className="p-8">
                <div className="text-center space-y-4">
                  <p className="text-gray-700">
                    Se l'email <strong>{email}</strong> è registrata nel nostro sistema, 
                    riceverai un link per reimpostare la password.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left">
                    <p className="text-sm text-blue-800">
                      <strong>💡 Suggerimento:</strong> Controlla anche la cartella spam se non vedi l'email.
                      Il link è valido per 30 minuti.
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl py-3 text-lg font-semibold"
                  >
                    Torna al Login
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-28 md:pb-8">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/login')}
            className="rounded-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center p-1">
              <img 
                src={agentAdvancerSenzaSfondo} 
                alt="InLeads Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <span className="font-semibold text-gray-900">InLeads</span>
          </div>
        </div>

        {/* Main Card */}
        <div className="max-w-md mx-auto">
          <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-8 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold">
                Password Dimenticata?
              </CardTitle>
              <p className="text-blue-100 mt-2">
                Inserisci la tua email per ricevere il link di reset
              </p>
            </CardHeader>

            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Field */}
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 block">
                    Email *
                  </Label>
                  <div className="relative">
                    <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError("");
                      }}
                      placeholder="la-tua-email@esempio.com"
                      className={`pl-10 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 ${
                        error ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : ''
                      }`}
                    />
                  </div>
                  {error && (
                    <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-800">
                    <strong>ℹ️ Come funziona:</strong> Riceverai un'email con un link 
                    per reimpostare la tua password. Il link sarà valido per 30 minuti.
                  </p>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl py-3 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Invia Link di Reset
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="flex justify-center items-center gap-6 flex-wrap text-gray-500 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Connessione sicura</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Dati protetti</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span>GDPR compliant</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
